import { match } from '@formatjs/intl-localematcher'
import { DEFAULT_LOCALE } from './locale'

export function isEnabledLocale(
  locale: string,
  enabledLocales?: Array<string>,
): boolean {
  return enabledLocales ? enabledLocales.includes(locale) : true
}

/**
 * Parse the Accept-Language header into an array of locale strings.
 * Returns locales sorted by quality value (highest first).
 *
 * @example
 * parseAcceptLanguage('en-US,en;q=0.9,fr;q=0.8')
 * // returns ['en-US', 'en', 'fr']
 */
export function parseAcceptLanguage(acceptLanguage: string): Array<string> {
  if (!acceptLanguage) {
    return []
  }

  return acceptLanguage
    .split(',')
    .map((part) => {
      const [locale, qualityStr] = part.trim().split(/\s*;\s*q\s*=\s*/)
      const quality = qualityStr ? parseFloat(qualityStr) : 1
      return { locale: locale.trim(), quality }
    })
    .filter(({ locale, quality }) => locale && !isNaN(quality))
    .sort((a, b) => b.quality - a.quality)
    .map(({ locale }) => locale)
}

/**
 * Match requested locales against supported locales using the best-fit algorithm.
 * Falls back to the default locale if no match is found.
 *
 * @param requestedLocales - Array of locale strings (e.g., from Accept-Language header)
 * @returns The best matching supported locale
 * @example
 * matchLocale(['en-US', 'en'])
 * // returns 'en-AU'
 *
 * @example
 * matchLocale(['zh-CN'])
 * // returns 'en-AU' (default fallback)
 */
export function matchLocale(
  requestedLocales: Array<string>,
  enabledLocales?: Array<string>,
): string {
  try {
    if (!enabledLocales) {
      return requestedLocales[0] || DEFAULT_LOCALE
    }

    if (enabledLocales.length === 0) {
      return DEFAULT_LOCALE
    }

    const matched = match(requestedLocales, enabledLocales, DEFAULT_LOCALE, {
      algorithm: 'best fit',
    })
    return matched
  } catch {
    // match() can throw if given invalid locale strings
    return DEFAULT_LOCALE
  }
}

/**
 * Match a locale from the Accept-Language header string.
 * Convenience function that combines parsing and matching.
 *
 * @param acceptLanguage - The Accept-Language header value
 * @returns The best matching supported locale
 * @example
 * matchLocaleFromHeader('en-US,en;q=0.9,fr;q=0.8')
 * // returns 'en-AU'
 */
export function matchLocaleFromHeader(
  acceptLanguage: string | undefined | null,
  enabledLocales?: Array<string>,
): string {
  if (!acceptLanguage) {
    return DEFAULT_LOCALE
  }

  const requestedLocales = parseAcceptLanguage(acceptLanguage)
  return matchLocale(requestedLocales, enabledLocales)
}

/**
 * Get the language code from a locale (e.g., 'en' from 'en-AU')
 */
export function getLanguageFromLocale(locale: string): string {
  return locale.split('-')[0]
}

/**
 * Get the region/country code from a locale (e.g., 'AU' from 'en-AU')
 */
export function getRegionFromLocale(locale: string): string {
  return locale.split('-')[1]
}
